import React from 'react';
import {
  useTable,
  usePagination,
  useBlockLayout,
  useResizeColumns,
  useSortBy,
} from 'react-table';

import styles from '../../../styles/sass/components/ReactTable.module.scss';

export interface Props {
  data: any[];
  columns: any[];
  pageSizeOptions: any;
  defaultPageSize: number;
  defaultSorted: any;
}

export const MonthlyMarketTable: React.FC<Props> = props => {
  const {
    data,
    columns,
    pageSizeOptions,
    defaultPageSize,
    defaultSorted,
  } = props;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    prepareRow,
    previousPage,
    nextPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: defaultPageSize,
        sortBy: defaultSorted,
      },
    },
    useSortBy,
    usePagination,
    useBlockLayout,
    useResizeColumns,
  );
  return (
    <div {...getTableProps()} className={styles.ReactTable}>
      <div className={styles.rtTable} role="grid">
        <div
          className={[styles.rtThead, styles.header].join(' ')}
          style={{ minWidth: 945 }}
        >
          {headerGroups.map((headerGroup: any) => (
            <div {...headerGroup.getHeaderGroupProps()} className={styles.rtTr}>
              {headerGroup.headers.map((column: any) => {
                return (
                  <div
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={`${[
                      styles.rtTh,
                      styles.rtResizeableHeader,
                      styles.cursorPointer,
                    ].join(' ')} ${column.isSorted ? styles.sortAsc : ''}`}
                    style={{
                      width: column.width,
                      maxWidth: column.width,
                      flex: `${column.width} 0 auto`,
                    }}
                  >
                    <div className={styles.rtResizableHeaderContent}>
                      {column.render('Header')}{' '}
                      <span style={{ textAlign: 'right' }}>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' 🔽'
                            : ' 🔼'
                          : ''}
                      </span>
                    </div>
                    <div
                      {...column.getResizerProps()}
                      className={styles.rtResizer}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()} className={styles.rtTbody}>
          {page.map((row: any, i: number) => {
            prepareRow(row);
            return (
              <div className={styles.rtTrGroup} role="rowgroup">
                <div
                  {...row.getRowProps()}
                  className={`${styles.rtTr} ${
                    i % 2 ? styles.even : styles.odd
                  }`}
                  style={{ width: '100%' }}
                >
                  {row.cells.map((cell: any, index: number) => {
                    return (
                      <div
                        {...cell.getCellProps()}
                        className={styles.rtTd}
                        style={{
                          width: cell.column.width,
                          maxWidth: cell.column.width,
                          flex: `${cell.column.width} 0 auto`,
                        }}
                      >
                        {cell.render('Cell')}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <div className={styles.pagination}>
          <div className={styles.previous}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              Previous
            </button>
          </div>
          <div className={styles.center}>
            <span className={styles.pageInfo}>
              Page
              <div className={styles.pageJump}>
                <input
                  aria-label="jump to page"
                  type="number"
                  value={pageIndex + 1}
                  onChange={e => {
                    const page = e.target.value
                      ? Number(e.target.value) - 1
                      : 0;
                    gotoPage(page);
                  }}
                />
              </div>
              of
              <span className={styles.totalPages}>7</span>
            </span>
            <span className={styles.pageSizeOptions}>
              <select
                aria-label="rows per page"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {pageSizeOptions.map((pageSize: any) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize} rows
                  </option>
                ))}
              </select>
            </span>
          </div>
          <div className={styles.next}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyMarketTable;
